import React from 'react';
import {connect} from 'react-redux';
import _ from 'lodash';

import {default as Modal, withModal} from 'components/Modal';
import VisibleIf from 'components/VisibleIf';
import Warning from 'components/Warning';
import ExternalLink from 'components/ExternalLink';

import {
  Totals,
  ItemsTable,
  BaseInformation,
  RemainingCredit,
  ReceiveCashRefund,
  LessSection
} from './components';

import {requiresPermission, PERMISSIONS} from 'ducks/auth';
import {
  MODALS as BASE_MODALS,
  openModal,
  closeModal,
  storeDataInModal
} from 'ducks/modals';

import {
  MODALS,
  fetchOverpayment,
  FETCH_SCHEDULE_DELAY,
  SCHEDULE_ITEM_TYPES,
  voidObject
} from 'ducks/tenancy';

import {delayedAction, composeActions} from 'ducks/common';
import PaymentStatusTag from 'components/PaymentStatusTag';

import './styles.css';

class OverpaymentDetailModal extends React.Component<any> {
  componentDidMount() {
    const {data: {overpaymentId, locationRef}} = this.props;

    this.props.requiresPermission(
      {permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS, locationRef},
      () => {
        const fetchOverpaymentExtraAction = this.props.storeDataExtraActions(
          data => ({
            overpayment: data
          })
        );
        this.props.fetchOverpayment(
          {overpaymentId},
          fetchOverpaymentExtraAction
        );
      }
    );
  }

  getExtraActions = () => {
    return [
      ...this.props.closeModalExtraActions,
      ...this.props.refetchExtraActions({
        tenancyId: this.props.data.tenancyId,
        scheduleType: this.props.data.scheduleType
      })
    ];
  };

  openRefundDetailModal = refundId =>
    this.props.requiresPermission(
      {
        locationRef: this.props.data.locationRef,
        permission: PERMISSIONS.VIEW_SCHEDULE_ITEM_DETAILS
      },
      () => {
        const {scheduleType, locationRef, tenancyId} = this.props.data;

        this.props.composeActions([
          () => closeModal(MODALS.OVERPAYMENT_DETAIL),
          () =>
            openModal(
              MODALS.REFUND_DETAIL,
              {
                scheduleType,
                refundId,
                locationRef,
                tenancyId
              },
              true
            )
        ]);
      }
    );

  openVoidOverpaymentModal = () => {
    const {
      data: {overpayment, scheduleType, tenancyId, locationRef}
    } = this.props;

    this.props.requiresPermission(
      {locationRef, permission: PERMISSIONS.VOID_SCHEDULE_ITEMS},
      () => {
        const confirmAction = () => {
          const delayedFetchScheduleAction = () =>
            delayedAction({
              delay: FETCH_SCHEDULE_DELAY,
              action: () =>
                composeActions(
                  this.props.delayExtraActions({
                    tenancyId,
                    scheduleType
                  })
                )
            });

          const successExtraActions = [
            () => closeModal(BASE_MODALS.CONFIRM),
            () => closeModal(MODALS.OVERPAYMENT_DETAIL),
            ...this.props.refetchExtraActions({
              tenancyId,
              scheduleType
            }),
            delayedFetchScheduleAction
          ];
          const failureExtraAction = [
            ({errors}) =>
              storeDataInModal({
                modalName: BASE_MODALS.CONFIRM,
                data: {errors, submitting: false}
              })
          ];

          this.props.voidObject(
            {
              objectId: overpayment.id,
              objectType: SCHEDULE_ITEM_TYPES.OVERPAYMENT,
              tenancyId,
              scheduleType
            },
            successExtraActions,
            failureExtraAction
          );
        };

        const noXeroLink = _.isEmpty(overpayment.xeroLink);
        const question = (
          <div>
            <Warning className="voidOverpaymentWarning">
              Due to Xero limitations you will need to manually void this
              overpayment in Xero as well. Click {noXeroLink && 'here'}
              {!noXeroLink && (
                <ExternalLink href={overpayment.xeroLink} targetBlank>
                  here
                </ExternalLink>
              )}{' '}
              to open it in Xero
            </Warning>
            Are you sure you want to void this overpayment?
          </div>
        );

        this.props.openModal(BASE_MODALS.CONFIRM, {
          heading: 'Void Overpayment',
          cancelButtonText: 'Cancel',
          confirmAction,
          question
        });
      }
    );
  };

  render() {
    const {
      data: {overpayment, scheduleType, locationRef, tenancyId}
    } = this.props;
    const xeroSyncErrors = _.get(overpayment, 'xeroErrors', []);

    return (
      <Modal
        show={true}
        onClose={this.props.closeModal}
        className="OverpaymentDetailModal">
        <Modal.Header>
          <h3 className="header">Overpayment Detail</h3>
          {overpayment &&
            overpayment.voided && (
              <div className="status">
                <PaymentStatusTag status={PaymentStatusTag.VOIDED} />
              </div>
            )}
        </Modal.Header>
        <Modal.Body>
          <Modal.Errors errors={xeroSyncErrors} />

          {overpayment && (
            <div>
              <BaseInformation
                overpayment={overpayment}
                openVoidOverpaymentModal={this.openVoidOverpaymentModal}
              />
              <ItemsTable overpayment={overpayment} />
              <Totals overpayment={overpayment} />
              <LessSection
                refunds={overpayment.refunds}
                openRefundDetailModal={this.openRefundDetailModal}
              />
              <RemainingCredit overpayment={overpayment} />
              {overpayment.remainingCredit !== 0.0 && (
                <VisibleIf
                  permission={PERMISSIONS.ADD_REFUND}
                  locationRef={locationRef}>
                  <ReceiveCashRefund
                    refetchExtraActions={this.props.refetchExtraActions}
                    tenancyId={tenancyId}
                    overpaymentId={overpayment.id}
                    getExtraActions={this.getExtraActions}
                    storeDataExtraActions={this.props.storeDataExtraActions}
                    scheduleType={scheduleType}
                    locationRef={locationRef}
                  />
                </VisibleIf>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>
    );
  }
}

const mapDispatchToProps = {
  fetchOverpayment,
  requiresPermission,
  openModal,
  voidObject,
  delayedAction,
  composeActions
};

export default connect(null, mapDispatchToProps)(withModal(
  MODALS.OVERPAYMENT_DETAIL
)(OverpaymentDetailModal) as React.ComponentType<any>);
